<script lang="ts">
	import type { ChatMessage, InSessionData, MessageActionType } from '@repo/commons/models'
	import { MessageItem, EditMessage } from './index.ts'
	import type { RetrievedDocument } from 'chatbridge-client/src'
	import { chatMessageAppear } from '@repo/commons/utils'
	import { chatT } from '@repo/commons/i18n/chat'

	interface Props {
		messages: ChatMessage[]
		showAvatar?: boolean
		editingMessageIds?: string[]
		allowMessageEditing?: boolean
		showFeedbackButtons?: boolean
		showMessageActions?: boolean
		enableTypingAnimation?: boolean
		onSubmitEditing?: (message: ChatMessage) => void
		onCancelEditing?: () => void
		onSessionDataClick?: (inSessionData: InSessionData | undefined) => void
		onButtonClick?:
			| (({ interactionId, buttonId }: { interactionId: string; buttonId: string }) => void)
			| null
		onFormSubmit?: (formData: Record<string, any>) => void
		onActionClick?: ((action: MessageActionType, interactionId: string) => void) | null
		// Shows a "Start a new chat" link under the conversation-end marker
		onStartNewChat?: (() => void) | null
		onCustomComponentClicked:
			| ((id: string, componentType: string, props: Record<string, any>) => void)
			| null
		onDocumentClick?: ((doc: RetrievedDocument) => void) | null
		onImageLoad?: ((imageFileName: string) => Promise<Blob | string>) | null
		onOpenImageModal?: ((imageUrl: string) => void) | null
		processImages?: boolean
		animationDuration?: number
	}

	let {
		messages,
		showAvatar = true,
		editingMessageIds = [],
		allowMessageEditing = true,
		showFeedbackButtons = true,
		showMessageActions = true,
		enableTypingAnimation = false,
		onButtonClick = null,
		onFormSubmit,
		onSessionDataClick,
		onSubmitEditing,
		onCancelEditing,
		onActionClick = null,
		onStartNewChat = null,
		onCustomComponentClicked = null,
		onDocumentClick = null,
		onImageLoad = null,
		onOpenImageModal = null,
		processImages = false,
		animationDuration = 500
	}: Props = $props()

	const isEditing = (message: ChatMessage) =>
		editingMessageIds.length > 0 && editingMessageIds.includes(message?.interactionId || '')
</script>

<div class="messages-container">
	{#each messages as message, index (message.interactionId ?? index)}
		<div
			transition:chatMessageAppear={{ duration: animationDuration, delay: 0 }}
			class="message-wrapper"
		>
			{#if message.isConversationEnd}
				<div class="conversation-end">
					<span class="conversation-end-badge">
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="1.75"
							stroke-linecap="round"
							stroke-linejoin="round"
							aria-hidden="true"
						>
							<path
								d="M20 12.5c0 4.14-3.58 7.5-8 7.5a8.9 8.9 0 0 1-3.6-.75L4 20.5l1.2-3.6A7.2 7.2 0 0 1 4 12.5C4 8.36 7.58 5 12 5s8 3.36 8 7.5Z"
							/>
							<path d="m9 12.75 2 2 4-4.25" />
						</svg>
					</span>
					<p>{$chatT('Conversation.Ended')}</p>
					{#if onStartNewChat}
						<button type="button" class="conversation-end-link" onclick={() => onStartNewChat?.()}>
							{$chatT('Conversation.StartNew')}
						</button>
					{/if}
				</div>
			{:else if isEditing(message)}
				<EditMessage {message} onSubmit={onSubmitEditing} onCancel={onCancelEditing} />
			{:else}
				<MessageItem
					{message}
					{showAvatar}
					{allowMessageEditing}
					{showFeedbackButtons}
					{showMessageActions}
					{enableTypingAnimation}
					{onButtonClick}
					{onFormSubmit}
					{onSessionDataClick}
					{onActionClick}
					{onCustomComponentClicked}
					{onDocumentClick}
					{onImageLoad}
					{onOpenImageModal}
					{processImages}
				/>
			{/if}
		</div>
	{/each}
</div>

<style>
	.messages-container {
		@apply flex flex-col items-start gap-6 py-4 w-full;
	}

	.message-wrapper {
		@apply w-full;
		will-change: transform, opacity;
	}

	.conversation-end {
		@apply flex flex-col items-center gap-2 w-full py-2 text-center text-[12px] leading-4 text-[rgba(0,44,77,0.5)];
	}

	.conversation-end-badge {
		@apply flex items-center justify-center w-9 h-9 rounded-full bg-[rgba(0,73,128,0.07)] text-[rgba(0,44,77,0.6)];
	}

	.conversation-end-badge svg {
		@apply w-5 h-5;
	}

	.conversation-end-link {
		@apply text-[12px] leading-4 font-medium underline underline-offset-2 cursor-pointer;
		color: var(--chat-button-color, #007acc);
	}

	.conversation-end-link:hover {
		@apply opacity-80;
	}

	.conversation-end p {
		@apply max-w-[260px];
	}
</style>
